import { ReportRequest } from '@/client/reports';
import { Subjects } from '@/client/users';
import { useKeyStatsReports } from '@/hooks/query/reports.hook';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { PhishingDashboardCard } from '@/ui/phishing-dashboard-card';
import { FlexContainer } from '@/ui/styled-ui';
import {
  colorBlackMain,
  colorGrayDark,
  colorGreenBorder,
  colorGreenChartDark,
  colorGreenChartMiddle,
  colorGreenChartPale,
  colorGreenRadarLightBorder,
  colorGreenRadarMiddleBorder,
  colorWhiteMain,
} from '@/utils/helpers/css-variables.helper';
import {
  CampaignClickedIcon,
  CampaignReportedPhishingIcon,
  CampaignSubmittedIcon,
  EmailSentIcon,
} from '@/utils/helpers/icons.helper';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledKeyStatistics = styled(FlexContainer)`
  overflow-x: scroll;
  ::-webkit-scrollbar-track,
  ::-webkit-scrollbar-corner {
    background-color: var(--beige-main);
  }
  ::-webkit-scrollbar-thumb {
    border-color: var(--beige-main);
  }
`;

type KeyStatisticsProps = {
  filters: ReportRequest;
};

export const KeyStatistics = ({ filters }: KeyStatisticsProps) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const docStyle = getComputedStyle(document.documentElement);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const hasReportingEnabled = canUseFeature(
    Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING,
  );

  const { keyStats, isLoading } = useKeyStatsReports(
    currentAccount?.id as string,
    filters,
  );

  return (
    <StyledKeyStatistics
      gap={16}
      className="mt-4"
      align="stretch"
      justify="space-between"
    >
      {isLoading || !keyStats ? (
        <>
          <Skeleton width="100%" height="210px" />
          <Skeleton width="100%" height="210px" />
          <Skeleton width="100%" height="210px" />
          {hasReportingEnabled && <Skeleton width="100%" height="210px" />}
        </>
      ) : (
        <>
          <PhishingDashboardCard
            id="total-emails-sent"
            color={colorBlackMain(docStyle)}
            backgroundColor={colorWhiteMain(docStyle)}
            borderColor={colorGrayDark(docStyle)}
            title={t('phishing.analytics.keyStats.totalEmailsSent')}
            icon={<EmailSentIcon />}
            value={Number(keyStats.sentCount)}
            description={t(
              'phishing.analytics.keyStats.totalEmailsSent.description',
              { recommendedCount: 8 * Number(keyStats.usersCount) },
            )}
          />
          <PhishingDashboardCard
            id="average-click-rate"
            color={colorBlackMain(docStyle)}
            backgroundColor={colorGreenChartPale(docStyle)}
            borderColor={colorGreenRadarLightBorder(docStyle)}
            title={t('phishing.analytics.keyStats.averageClickRate')}
            icon={<CampaignClickedIcon />}
            value={Number(keyStats.clickedRate)}
            isPercentage
            description={t(
              'phishing.analytics.keyStats.averageClickRate.description',
            )}
          />
          <PhishingDashboardCard
            id="average-data-submit-rate"
            color={colorWhiteMain(docStyle)}
            backgroundColor={colorGreenChartMiddle(docStyle)}
            borderColor={colorGreenRadarMiddleBorder(docStyle)}
            title={t('phishing.analytics.keyStats.averageDataSubmitRate')}
            icon={<CampaignSubmittedIcon />}
            value={Number(keyStats.submittedRate)}
            isPercentage
            description={t(
              'phishing.analytics.keyStats.averageDataSubmitRate.description',
            )}
          />
          {hasReportingEnabled && (
            <PhishingDashboardCard
              id="average-report-rate"
              color={colorWhiteMain(docStyle)}
              backgroundColor={colorGreenChartDark(docStyle)}
              borderColor={colorGreenBorder(docStyle)}
              title={t('phishing.analytics.keyStats.averageReportRate')}
              icon={<CampaignReportedPhishingIcon />}
              value={Number(keyStats.reportedRate)}
              isPercentage
              description={t(
                'phishing.analytics.keyStats.averageReportRate.description',
              )}
            />
          )}
        </>
      )}
    </StyledKeyStatistics>
  );
};
